import Honeybadger from '@honeybadger-io/js';
import isArray from 'lodash-es/isArray';
import isBoolean from 'lodash-es/isBoolean';
import isNull from 'lodash-es/isNull';
import isNumber from 'lodash-es/isNumber';
import isObject from 'lodash-es/isObject';
import isString from 'lodash-es/isString';
import isUndefined from 'lodash-es/isUndefined';
import { match } from 'ts-pattern';
import { isDecodingError } from './Decoding';
import { isClientErrorStatus, isResponseError, isServerErrorStatus, } from './http';
const getUnknownErrorInfo = (error) => {
    if (error instanceof Error) {
        return {
            cause: error.cause,
            isError: true,
            message: error.message,
            stack: error.stack,
        };
    }
    if (error instanceof ErrorEvent) {
        return getUnknownErrorInfo(error.error);
    }
    try {
        return {
            isError: false,
            message: JSON.stringify(error),
        };
    }
    catch (jsonError) {
        return getUnknownErrorInfo(jsonError);
    }
};
/**
 * Replaces real values with their types in the payload.
 * This prevents exposing sensitive data in the error reports.
 */
const parseErrorPayload = (payload) => {
    if (isBoolean(payload) ||
        isNumber(payload) ||
        isString(payload) ||
        isUndefined(payload)) {
        return typeof payload;
    }
    if (isNull(payload)) {
        return 'null';
    }
    if (isArray(payload)) {
        return payload.map(parseErrorPayload);
    }
    if (isObject(payload)) {
        return Object.entries(payload).reduce((accumulator, [key, value]) => ({
            ...accumulator,
            [key]: parseErrorPayload(value),
        }), {});
    }
    return 'unknown';
};
/**
 * Generates the error id and name based on the given error instance,
 * then extracts the necessary error data for a report,
 * and finally sends the report to HoneyBadger.
 *
 * @example
 * ```ts
 * import { Observability } from "@catawiki/runtime-toolkit";
 *
 * try {
 *   // sometthing that throws an error
 * } catch (error) {
 *   Observability.notifyHB(error, { fingerprint: "my_fingerprint" });
 * }
 *
 * // Custom error
 * Observability.notifyHB(new Error('message'), { fingerprint: "my_fingerprint" });
 * ```
 */
export const notifyHB = (error, options = {}) => {
    const { name: errorName = error instanceof Error ? error.name : 'UnknownError', fingerprint, } = options;
    const name = fingerprint ? `${errorName}: ${fingerprint}` : errorName;
    match(error)
        .when(isResponseError, (httpError) => {
        if (isClientErrorStatus(httpError.status) ||
            isServerErrorStatus(httpError.status)) {
            return;
        }
        Honeybadger.notify({
            ...options,
            context: {
                ...options.context,
                payload: parseErrorPayload(httpError.payload),
                status: httpError.status,
                url: httpError.url,
            },
            message: options.message || httpError.message || `Unknown HTTP error`,
            name,
            stack: httpError.stack,
        });
    })
        .when(isDecodingError, (decodingError) => {
        Honeybadger.notify({
            ...options,
            context: {
                ...options.context,
                errors: decodingError.errors,
                payload: parseErrorPayload(decodingError.payload),
            },
            message: options.message ||
                decodingError.message ||
                `Failed to decode on: ${fingerprint}`,
            name,
            stack: decodingError.stack,
        });
    })
        .otherwise((unknownError) => {
        const { cause, isError, message, stack } = getUnknownErrorInfo(unknownError);
        Honeybadger.notify({
            ...options,
            cause,
            context: { ...options.context, isError },
            message: options.message || message || `Unknown error on: ${fingerprint}`,
            name,
            stack,
        });
    });
};
